import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import apis from '@src/apis';
import i18n from '@src/languages';
import {
  COMMUNITY_VOICES,
  VBEE_VOICES,
  COMMUNITY_SECTION,
  VOICE_SCOPE,
  COMMUNITY_SORT_TYPE,
  MY_VOICES,
  FAVOURITE_VOICES,
} from '@src/constants/voiceLibrary';
import {
  VOICE_FEATURED_PAGINATION_LIMIT,
  VOICE_OWNERSHIP,
  VOICE_TRENDING_PAGINATION_LIMIT,
  VOICE_NEWEST_PAGINATION_LIMIT,
  VOICE_LOAD_MORE_PAGINATION_LIMIT,
  VOICE_LOAD_MORE_PAGINATION_LIMIT_TRENDING,
  SORT_VOICE_OPTIONS_VBEE,
  DEFAULT_RETENTION_DAYS,
  SORT_VOICE_OPTIONS_MY_VOICE,
  SORT_VOICE_OPTIONS_COMMUNITY,
  SORT_VOICE_OPTIONS_FAVOURITE,
} from '@src/constants/voice';
import { Box } from '@mui/material';
import useHandleFavouriteVoices from '@src/hooks/useHandleFavouriteVoices';
import Toolbar from '@src/containers/VoiceLibrary/Toolbar';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import TrendingVoices from '@src/containers/VoiceLibrary/Content/TrendingVoices';
import FeaturedVoice from '@src/containers/VoiceLibrary/Content/FeaturedVoice';
import NewestVoice from '@src/containers/VoiceLibrary/Content/NewestVoice';
import VoiceResultHeader from '@src/containers/VoiceLibrary/Content/VoiceResultHeader';
import VoiceList from '@src/containers/VoiceLibrary/Content/VoiceList';
import { VOICE_STATUS } from '@src/constants/voiceCloning';
import VoiceTabSelection from './VoiceTabSelection';
import { StyledVoiceSelectionDialog } from './index.style';

const INITIAL_DATA_FILTER = {
  language: { name: { vi: 'Tất cả', en: 'All' }, primaryLanguageSubTag: '' },
  region: '',
  sortBy: '',
  categories: [],
  advancedFilter: {
    ageGroup: [],
    gender: [],
    retentionDays: [],
    level: [],
    voiceCloningType: [],
  },
  search: '',
};

const LIST_COMMUNITY_VOICES = {
  featuredVoices: [],
  trendingVoices: [],
  newestVoices: [],
};

const SECTION_WITHOUT_TOOLBAR = [
  COMMUNITY_SECTION.FEATURED_VOICE,
  COMMUNITY_SECTION.TRENDING_VOICE,
];

const getInitialSortBy = (tab) => {
  if (tab === VBEE_VOICES) return SORT_VOICE_OPTIONS_VBEE[0].value;
  if (tab === MY_VOICES) return SORT_VOICE_OPTIONS_MY_VOICE[0].value;
  if (tab === FAVOURITE_VOICES) return SORT_VOICE_OPTIONS_FAVOURITE[0].value;
  return '';
};

const VoiceSelectionDialog = ({
  open,
  onClose,
  onChangeVoice,
  isApplyPreset,
  isDubbing,
  activeVoiceCode,
  voiceStatus,
  isOpenFromSpeakerSettings = false,
  onCloseWhenCreateVoice,
  provider,
  forceLanguage,
  multiLanguage,
}) => {
  const { t } = useTranslation();
  const { language: currentLang } = i18n;

  const contentRef = useRef(null);

  const getInitialTab = useMemo(() => {
    if (!activeVoiceCode) {
      return VBEE_VOICES;
    }
    const isClonedVoiceParam = isClonedVoiceCode(activeVoiceCode);
    if (voiceStatus === VOICE_STATUS.PUBLIC) {
      return COMMUNITY_VOICES;
    }
    return isClonedVoiceParam ? MY_VOICES : VBEE_VOICES;
  }, [activeVoiceCode]);

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [typeViewSection, setTypeViewSection] = useState(null);
  const [previousEvent, setPreviousEvent] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialTabLoading, setInitialTabLoading] = useState(true);
  const [hasLoadedFavouriteVoices, setHasLoadedFavouriteVoices] =
    useState(false);
  const [voicesList, setVoicesList] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [dataFilter, setDataFilter] = useState(() => ({
    ...INITIAL_DATA_FILTER,
    sortBy: getInitialSortBy(getInitialTab),
  }));
  const [searchValueView, setSearchValueView] = useState('');
  const [listCommunityVoices, setListCommunityVoices] = useState(
    LIST_COMMUNITY_VOICES,
  );
  const [favouriteVoicesList, setFavouriteVoicesList] = useState([]);
  const [favouriteVoiceCodes, setFavouriteVoiceCodes] = useState([]);
  const {
    globalCurrentFavouriteVoiceCodes,
    handleFavoriteToggleRequest,
    addToQueuePostProcessingFavouriteVoices,
    setGlobalCurrentFavouriteVoiceCodes,
    handleErrorProcessingFavouriteVoices,
  } = useHandleFavouriteVoices({ favouriteVoiceCodes });

  const isCommunity = activeTab === COMMUNITY_VOICES;
  const isVbee = activeTab === VBEE_VOICES;
  const isMyVoice = activeTab === MY_VOICES;
  const isFavourite = activeTab === FAVOURITE_VOICES;
  const controllerRef = useRef(null);

  const isViewingAllSection = SECTION_WITHOUT_TOOLBAR.includes(typeViewSection);
  const isViewingFilterResult =
    typeViewSection === COMMUNITY_SECTION.NEWEST_VOICE;

  const voicesListFinal = useMemo(() => {
    if (typeViewSection === COMMUNITY_SECTION.TRENDING_VOICE) {
      return voicesList.filter((voice) => voice?.stats?.totalCredits > 0);
    }
    return voicesList;
  }, [voicesList, typeViewSection]);

  const isFiltering = useMemo(
    () =>
      (dataFilter.sortBy && !isVbee && !isMyVoice && !isFavourite) ||
      (dataFilter.language && dataFilter.language?.primaryLanguageSubTag) ||
      dataFilter.region ||
      dataFilter.categories?.length > 0 ||
      dataFilter.advancedFilter?.ageGroup?.length > 0 ||
      dataFilter.advancedFilter?.gender?.length > 0 ||
      (dataFilter.advancedFilter?.retentionDays?.length > 0 &&
        dataFilter.advancedFilter?.retentionDays[0] !==
          DEFAULT_RETENTION_DAYS) ||
      dataFilter.advancedFilter?.level?.length > 0 ||
      dataFilter.advancedFilter?.voiceCloningType?.length > 0 ||
      dataFilter.search,
    [dataFilter, isVbee, isMyVoice, isFavourite],
  );

  const isCommunityView =
    isCommunity &&
    !isViewingAllSection &&
    !isViewingFilterResult &&
    !isFiltering &&
    !searchValueView;
  const hasButtonBack = isViewingAllSection && !isVbee;

  const getTitleResultHeader = useCallback(() => {
    const titles = {
      [COMMUNITY_SECTION.FEATURED_VOICE]: t('featuredVoice'),
      [COMMUNITY_SECTION.TRENDING_VOICE]: t('trendingVoice'),
    };
    if (titles[typeViewSection]) return titles[typeViewSection];
    if (isVbee && !isFiltering) return t('vbeeVoice');
    if (isMyVoice && !isFiltering) return t('myVoice');
    return t('result');
  }, [typeViewSection, isVbee, isFiltering, t, isMyVoice]);

  const handleViewAllSection = useCallback((type) => {
    if (type === COMMUNITY_SECTION.NEWEST_VOICE) {
      setDataFilter((prev) => ({
        ...prev,
        sortBy: 'publishAt_desc',
      }));
    }
    setTypeViewSection(type);
  }, []);

  const handleBackCommunityVoice = useCallback(() => {
    setTypeViewSection(null);
  }, []);

  const fetchLeaderboard = async () => {
    const data = await apis.leaderboard.getLeaderboard();
    if (data?.status) {
      setPreviousEvent(data.result.previousEvent);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const fetchLanguages = async () => {
      const data = await apis.language.getLanguages({
        voiceOwnership:
          (isCommunity || (isDubbing && !multiLanguage)) &&
          VOICE_OWNERSHIP.COMMUNITY,
      });
      if (data?.status) setLanguages(data.result?.languages || []);
    };
    fetchLanguages();
  }, [isCommunity, isDubbing]);

  const fetchVoices = useCallback(
    (params, options) => apis.voices.getVoicesV3(params, options),
    [],
  );

  const paramsFetchVoices = useMemo(
    () => ({
      trendingVoice: {
        limit: VOICE_TRENDING_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        sort: [
          COMMUNITY_SORT_TYPE.TRENDING_30_DAYS,
          COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
        ].join(','),
        ...(isDubbing && { hasDubbing: isDubbing }),
      },
      featuredVoice: {
        limit: VOICE_FEATURED_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        scope: VOICE_SCOPE.FEATURED,
        sort: [
          COMMUNITY_SORT_TYPE.TOTAL_CREDITS_DESC,
          COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
        ].join(','),
        ...(isDubbing && { hasDubbing: isDubbing }),
      },
      newestVoice: {
        limit: VOICE_NEWEST_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        sort: COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
        ...(isDubbing && { hasDubbing: isDubbing }),
      },
      vbeeVoice: {
        voiceOwnership: VOICE_OWNERSHIP.VBEE,
        ...(isDubbing && { hasDubbing: isDubbing }),
      },
    }),
    [],
  );

  const fetchCommunityVoices = useCallback(async () => {
    const [dataFeatured, dataTrending, dataNewest] = await Promise.all([
      fetchVoices(paramsFetchVoices.featuredVoice),
      fetchVoices(paramsFetchVoices.trendingVoice),
      fetchVoices(paramsFetchVoices.newestVoice),
    ]);
    if (dataFeatured?.status && dataTrending?.status && dataNewest?.status) {
      setListCommunityVoices({
        featuredVoices: dataFeatured.result?.voices || [],
        trendingVoices: dataTrending.result?.voices || [],
        newestVoices: dataNewest.result?.voices || [],
      });
    }
    setInitialTabLoading(false);
  }, [fetchVoices, paramsFetchVoices]);

  const voiceOwnership = useMemo(() => {
    if (isVbee) return VOICE_OWNERSHIP.VBEE;
    if (isMyVoice) return VOICE_OWNERSHIP.PERSONAL;
    return VOICE_OWNERSHIP.COMMUNITY;
  }, [isVbee, isMyVoice, isCommunity]);

  const getListVoicesLoadMore = useCallback(
    async (isLoadMore = false) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      const isVietnamese = dataFilter.language?.primaryLanguageSubTag === 'vi';
      const paramsFilterLanguage = {
        language: dataFilter.language?.primaryLanguageSubTag,
        ...(isVietnamese
          ? { locale: dataFilter?.region }
          : { region: dataFilter?.region }),
      };
      const paramsFilterCategory =
        dataFilter.categories?.length > 0
          ? { category: dataFilter.categories.join(',') }
          : {};
      const paramsFilterAdvancedFilter = {
        ageGroup: dataFilter.advancedFilter.ageGroup?.join(','),
        gender: dataFilter.advancedFilter.gender?.join(','),
        retentionDays: dataFilter.advancedFilter.retentionDays?.join(','),
      };
      const paramsFilterLevel =
        dataFilter.advancedFilter.level?.length > 0
          ? { level: dataFilter.advancedFilter.level.join(',') }
          : {};
      const voiceCloningType = dataFilter.advancedFilter?.voiceCloningType;
      const paramsFilterVoiceCloningType =
        voiceCloningType?.length > 0
          ? { voiceCloningType: voiceCloningType.join(',') }
          : {};

      setIsLoading(true);
      const data = await fetchVoices(
        {
          ...paramsFetchVoices[typeViewSection || 'vbeeVoice'],
          ...(isLoadMore && { cursor: nextCursor }),
          limit:
            typeViewSection === COMMUNITY_SECTION.TRENDING_VOICE
              ? VOICE_LOAD_MORE_PAGINATION_LIMIT_TRENDING
              : VOICE_LOAD_MORE_PAGINATION_LIMIT,
          ...(dataFilter.sortBy && { sort: dataFilter.sortBy }),
          voiceOwnership,
          ...paramsFilterLanguage,
          ...paramsFilterCategory,
          ...paramsFilterAdvancedFilter,
          ...paramsFilterLevel,
          ...paramsFilterVoiceCloningType,
          search: dataFilter.search,
          hasDubbing: isDubbing,
          ...(forceLanguage && { language: forceLanguage }),
          ...(provider && isVbee && { provider }),
        },
        { signal: controller.signal },
      );

      if (data?.status) {
        setHasNextPage(data.result?.pagination?.hasNextPage);
        setNextCursor(data.result?.pagination?.nextCursor);
        setVoicesList((prev) =>
          isLoadMore
            ? [...prev, ...(data.result?.voices || [])]
            : data.result?.voices || [],
        );
      }
      setIsLoading(false);
      setInitialTabLoading(false);
    },
    [
      dataFilter,
      fetchVoices,
      nextCursor,
      paramsFetchVoices,
      typeViewSection,
      provider,
      isVbee,
      forceLanguage,
    ],
  );

  const fetchFavouriteVoices = useCallback(async () => {
    setIsLoading(true);
    const isVietnamese = dataFilter.language?.primaryLanguageSubTag === 'vi';
    const paramsFilterLanguage = {
      language: dataFilter.language?.primaryLanguageSubTag,
      ...(isVietnamese
        ? { locale: dataFilter?.region }
        : { region: dataFilter?.region }),
    };
    const paramsFilterCategory =
      dataFilter.categories?.length > 0
        ? { category: dataFilter.categories.join(',') }
        : {};
    const paramsFilterAdvancedFilter = {
      ageGroup: dataFilter.advancedFilter.ageGroup?.join(','),
      gender: dataFilter.advancedFilter.gender?.join(','),
    };

    // Check if the sortBy is valid for favourite voices
    // This is to prevent when user is in other tabs and then add/remove voice from favourite list
    // which will refetch the favourite voices list with the wrong sortBy.
    let sortBy;
    const isValidSortFavourite = SORT_VOICE_OPTIONS_FAVOURITE.some((option) =>
      isEqual(option.value, dataFilter.sortBy),
    );
    if (isValidSortFavourite) {
      sortBy = dataFilter.sortBy;
    } else {
      sortBy = SORT_VOICE_OPTIONS_FAVOURITE[0].value;
    }
    const paramsFilterSort = {
      sort: sortBy,
    };

    const data = await apis.favouriteVoices.getFavouriteVoices({
      ...paramsFilterLanguage,
      ...paramsFilterCategory,
      ...paramsFilterAdvancedFilter,
      ...paramsFilterSort,
      ...(dataFilter.search ? { search: dataFilter.search } : {}),
      ...(dataFilter.search
        ? { searchFields: 'name,code,description,category' }
        : {}),
      ...(dataFilter.search ? { lang: currentLang } : {}),
      ...(isDubbing && { hasDubbing: isDubbing }),
    });
    if (data?.status) {
      setFavouriteVoicesList(data.result?.favouriteVoices || []);
      setFavouriteVoiceCodes(
        data.result?.favouriteVoices?.map((voice) => voice.code) || [],
      );

      if (!hasLoadedFavouriteVoices) {
        setGlobalCurrentFavouriteVoiceCodes(
          data.result?.favouriteVoices?.map((voice) => voice.code) || [],
        );
      }
    }
    setIsLoading(false);
    setHasLoadedFavouriteVoices(true);
  }, [dataFilter, hasLoadedFavouriteVoices]);

  useEffect(() => {
    if (isFavourite) {
      setFavouriteVoicesList([]);
      setFavouriteVoiceCodes([]);
      fetchFavouriteVoices();
      return;
    }

    if (isCommunity && !typeViewSection && !isFiltering) {
      return;
    }

    setVoicesList([]);
    setNextCursor('');
    setHasNextPage(false);
    getListVoicesLoadMore();
  }, [
    typeViewSection,
    isVbee,
    isMyVoice,
    isCommunity,
    dataFilter,
    isFavourite,
  ]);

  const resetDataFilter = useCallback((tab) => {
    setDataFilter({
      ...INITIAL_DATA_FILTER,
      sortBy: getInitialSortBy(tab),
    });
    setSearchValueView('');
    setInitialTabLoading(true);
  }, []);

  useEffect(() => {
    if (isCommunity && !typeViewSection && !isFiltering) {
      fetchCommunityVoices();
    }
  }, [isCommunity, typeViewSection, isFiltering]);

  useEffect(() => {
    if (!isDubbing) {
      fetchFavouriteVoices();
    }
  }, []);

  useEffect(() => {
    const refetchFavouriteVoices = async () => {
      const isAllowedToRefetch = !open;
      if (isAllowedToRefetch) {
        setHasLoadedFavouriteVoices(false);
        await fetchFavouriteVoices();
      }
    };
    refetchFavouriteVoices();
  }, [open]);

  const handleScrollToTop = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleChangeTab = useCallback(
    (tab) => {
      handleScrollToTop();

      const isAlreadyInDefaultCommunity =
        tab === COMMUNITY_VOICES &&
        activeTab === tab &&
        !typeViewSection &&
        !isFiltering;

      if (isAlreadyInDefaultCommunity) {
        fetchCommunityVoices();
        return;
      }

      setActiveTab(tab);
      setTypeViewSection(null);
      resetDataFilter(tab);
    },
    [
      activeTab,
      typeViewSection,
      isFiltering,
      resetDataFilter,
      fetchCommunityVoices,
      handleScrollToTop,
    ],
  );

  const dataLanguages = useMemo(() => {
    const allRegion = { name: { vi: 'Tất cả', en: 'All' }, regionSubTag: '' };
    const allLanguage = {
      name: { vi: 'Tất cả', en: 'All' },
      primaryLanguageSubTag: '',
    };

    return [allLanguage, ...languages].map((language) => {
      if (language?.primaryLanguageSubTag === 'vi') {
        return {
          ...language,
          regions: [
            allRegion,
            {
              name: { vi: 'Miền Bắc', en: 'Northern' },
              regionSubTag: 'northern',
            },
            {
              name: { vi: 'Miền Trung', en: 'Central' },
              regionSubTag: 'central',
            },
            {
              name: { vi: 'Miền Nam', en: 'Southern' },
              regionSubTag: 'southern',
            },
          ],
        };
      }
      return {
        ...language,
        regions: [allRegion, ...(language.regions || [])],
      };
    });
  }, [languages]);

  const handleChangeFilter = useCallback(
    (key, value) => {
      const initSortByCommunity =
        !isVbee &&
        value &&
        !dataFilter.sortBy &&
        SORT_VOICE_OPTIONS_COMMUNITY[0].value;

      if (key === 'language') {
        const selectedLanguage = dataLanguages.find(
          (lang) => lang.primaryLanguageSubTag === value,
        );
        setDataFilter((prev) => ({
          ...prev,
          region: '',
          language: selectedLanguage,
          ...(initSortByCommunity && {
            sortBy: initSortByCommunity,
          }),
        }));
      } else if (key === 'categories') {
        setDataFilter((prev) => {
          const categories = Array.isArray(prev.categories)
            ? [...prev.categories]
            : [];
          const idx = categories.indexOf(value);
          if (idx === -1) categories.push(value);
          else categories.splice(idx, 1);
          return {
            ...prev,
            ...(initSortByCommunity && {
              sortBy: initSortByCommunity,
            }),
            categories,
          };
        });
      } else {
        setDataFilter((prev) => ({
          ...prev,
          ...(initSortByCommunity && { sortBy: initSortByCommunity }),
          [key]: value,
        }));
      }
    },
    [dataLanguages, isVbee, isMyVoice, dataFilter.sortBy],
  );

  const shouldShowToolbar = SECTION_WITHOUT_TOOLBAR.includes(typeViewSection);

  const renderContent = () => (
    <StyledVoiceSelectionDialog ref={contentRef}>
      <>
        {!shouldShowToolbar && (
          <Box>
            <Toolbar
              dataLanguages={dataLanguages}
              dataFilter={dataFilter}
              handleChangeFilter={handleChangeFilter}
              isVbee={isVbee}
              resetDataFilter={resetDataFilter}
              isMyVoice={isMyVoice}
              activeTab={activeTab}
              searchValueView={searchValueView}
              setSearchValueView={setSearchValueView}
              isFavourite={isFavourite}
              isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
              onCloseWhenCreateVoice={onCloseWhenCreateVoice}
            />
          </Box>
        )}
        <>
          {isCommunityView &&
            !!listCommunityVoices.trendingVoices?.filter(
              (voice) => voice?.stats?.totalCredits > 0,
            ).length && (
              <Box className="community-voices-container-first-show">
                <TrendingVoices
                  onSeeAll={() =>
                    handleViewAllSection(COMMUNITY_SECTION.TRENDING_VOICE)
                  }
                  voicesList={listCommunityVoices.trendingVoices}
                  section={COMMUNITY_SECTION.TRENDING_VOICE}
                  onChangeVoice={onChangeVoice}
                  onClose={onClose}
                  isApplyPreset={isApplyPreset}
                  isCommunity={isCommunity}
                  isInitialLoading={initialTabLoading}
                  isDialog
                  onFetchFavouriteVoices={fetchFavouriteVoices}
                  favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
                  onFavoriteToggleRequest={handleFavoriteToggleRequest}
                  onHandleFavoriteProcessingComplete={
                    addToQueuePostProcessingFavouriteVoices
                  }
                  onErrorProcessingFavouriteVoices={
                    handleErrorProcessingFavouriteVoices
                  }
                  activeVoiceCode={activeVoiceCode}
                  prizes={previousEvent?.prizes}
                  isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
                />
              </Box>
            )}
          {isCommunityView && !!listCommunityVoices.featuredVoices.length && (
            <Box className="community-voices-container-first-show">
              <FeaturedVoice
                onSeeAll={() =>
                  handleViewAllSection(COMMUNITY_SECTION.FEATURED_VOICE)
                }
                voicesList={listCommunityVoices.featuredVoices}
                section={COMMUNITY_SECTION.FEATURED_VOICE}
                onChangeVoice={onChangeVoice}
                onClose={onClose}
                isApplyPreset={isApplyPreset}
                isCommunity={isCommunity}
                isInitialLoading={initialTabLoading}
                isDialog
                onFetchFavouriteVoices={fetchFavouriteVoices}
                favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
                onFavoriteToggleRequest={handleFavoriteToggleRequest}
                onHandleFavoriteProcessingComplete={
                  addToQueuePostProcessingFavouriteVoices
                }
                onErrorProcessingFavouriteVoices={
                  handleErrorProcessingFavouriteVoices
                }
                activeVoiceCode={activeVoiceCode}
                prizes={previousEvent?.prizes}
                isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
              />
            </Box>
          )}
          {isCommunityView && !!listCommunityVoices.newestVoices.length && (
            <NewestVoice
              onSeeAll={() =>
                handleViewAllSection(COMMUNITY_SECTION.NEWEST_VOICE)
              }
              voicesList={listCommunityVoices.newestVoices}
              section={COMMUNITY_SECTION.NEWEST_VOICE}
              onChangeVoice={onChangeVoice}
              onClose={onClose}
              isApplyPreset={isApplyPreset}
              isCommunity={isCommunity}
              isInitialLoading={initialTabLoading}
              isDialog
              onFetchFavouriteVoices={fetchFavouriteVoices}
              favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
              onFavoriteToggleRequest={handleFavoriteToggleRequest}
              onHandleFavoriteProcessingComplete={
                addToQueuePostProcessingFavouriteVoices
              }
              onErrorProcessingFavouriteVoices={
                handleErrorProcessingFavouriteVoices
              }
              activeVoiceCode={activeVoiceCode}
              prizes={previousEvent?.prizes}
              isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
            />
          )}
          {!isFavourite && (!isCommunityView || isVbee) && (
            <Box className="voice-list-container-result">
              <VoiceResultHeader
                title={getTitleResultHeader()}
                onBack={handleBackCommunityVoice}
                hasBack={hasButtonBack}
              />
              <VoiceList
                isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
                voices={voicesListFinal}
                isCard={false}
                isLoadMore={!isCommunityView || isVbee}
                hasMore={
                  hasNextPage &&
                  typeViewSection !== COMMUNITY_SECTION.TRENDING_VOICE
                }
                loadMore={() =>
                  getListVoicesLoadMore(
                    typeViewSection !== COMMUNITY_SECTION.TRENDING_VOICE,
                  )
                }
                onCloseWhenCreateVoice={onCloseWhenCreateVoice}
                isVbee={isVbee}
                isLoading={isLoading}
                isCommunity={isCommunity}
                isTrending
                onChangeVoice={onChangeVoice}
                onClose={onClose}
                isMyVoice={isMyVoice}
                isApplyPreset={isApplyPreset}
                isDialog
                onFetchFavouriteVoices={fetchFavouriteVoices}
                favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
                onFavoriteToggleRequest={handleFavoriteToggleRequest}
                onHandleFavoriteProcessingComplete={
                  addToQueuePostProcessingFavouriteVoices
                }
                onErrorProcessingFavouriteVoices={
                  handleErrorProcessingFavouriteVoices
                }
                activeVoiceCode={activeVoiceCode}
                prizes={previousEvent?.prizes}
              />
            </Box>
          )}
          {isFavourite && (
            <Box className="voice-list-container-result">
              <VoiceList
                voices={favouriteVoicesList}
                isCard={false}
                isLoadMore
                hasMore={false}
                loadMore={() => {}}
                isVbee={isVbee}
                isLoading={isLoading}
                isCommunity={isCommunity}
                isFavourite={isFavourite}
                isTrending
                isFiltering={isFiltering}
                onFetchFavouriteVoices={fetchFavouriteVoices}
                favouriteVoiceCodes={favouriteVoiceCodes}
                onFavoriteToggleRequest={handleFavoriteToggleRequest}
                onHandleFavoriteProcessingComplete={
                  addToQueuePostProcessingFavouriteVoices
                }
                onErrorProcessingFavouriteVoices={
                  handleErrorProcessingFavouriteVoices
                }
                onChangeVoice={onChangeVoice}
                onClose={onClose}
                activeVoiceCode={activeVoiceCode}
                prizes={previousEvent?.prizes}
                isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
              />
            </Box>
          )}
        </>
      </>
    </StyledVoiceSelectionDialog>
  );

  const renderTab = () => (
    <VoiceTabSelection
      activeTab={activeTab}
      handleChangeTab={handleChangeTab}
    />
  );

  return (
    <CommonDialog
      open={open}
      width="1200px"
      styleTitle={{
        paddingTitle: isDubbing ? '24px' : '12px 24px',
        height: '80px',
        closeIcon: {
          top: isDubbing && '20px',
        },
      }}
      PaperProps={{
        sx: {
          height: '700px',
        },
      }}
      onClose={onClose}
      renderTitle={renderTab()}
      contentComponent={renderContent()}
    />
  );
};

export default VoiceSelectionDialog;
